import { useLanguages } from '@api/application-configuration';
import { useLoginExternalMutation, useLoginMutation } from '@api/auth';
import { getEnvVars } from '@environment';
import { HeightAnimator } from '@unistocks/components/layout/height-animator';
import { Page } from '@unistocks/components/page/page';
import { Anchor } from '@unistocks/components/text/link';
import { Paragraph } from '@unistocks/components/text/text';
import { AuthContainer } from '@unistocks/features/authentication/auth-container';
import { AuthType } from '@unistocks/features/authentication/auth-type';
import { ExternalRegister } from '@unistocks/features/authentication/register/external-register';
import {
  RegisterForm,
  RegisterFormData
} from '@unistocks/features/authentication/register/register-form';
import { TermsAndConditionsAccept } from '@unistocks/features/authentication/register/terms-and-condition';
import { abpTenantFindTenantByName } from '@unistocks/proxy/axios-client/AbpTenantClient';
import { useAccountRegisterMutation } from '@unistocks/proxy/axios-client/AccountQuery';
import { useAppDispatch } from '@unistocks/shared/hooks/redux-hooks';
import { useActiveColorScheme } from '@unistocks/shared/hooks/use-active-color-scheme';
import { useSettingValue } from '@unistocks/shared/hooks/use-setting-value';
import { t } from '@unistocks/shared/localization/index.ts';
import { useSystemTimeZone } from '@unistocks/shared/localization/time-zone';
import { AbpSettings, UnistocksSettings } from '@unistocks/shared/settings';
import { AppActions } from '@unistocks/shared/store/root.slice';
import { style } from '@unistocks/shared/styles';
import { getDeviceLanguage } from '@unistocks/shared/utils/device-utils';
import { isTrue } from '@unistocks/shared/utils/util';
import { AuthTokenDto, ExternalAuthenticationDto } from '@unistocks/types/auth';
import { Link, useFocusEffect, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { AnimatePresence, isWeb, useMedia, View } from 'tamagui';

const { appName } = getEnvVars();

export const Register: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { setActiveColorScheme } = useActiveColorScheme();
  const media = useMedia();
  const emailConfirmationSetting = useSettingValue(AbpSettings.RequireConfirmedEmail);
  const isEmailConfirmationRequired = isTrue(emailConfirmationSetting);
  const supportedLanguages = useLanguages();
  const lang = getDeviceLanguage(supportedLanguages);
  const timeZone = useSystemTimeZone();
  const [mode, setMode] = useState<AuthType>('external');
  const isExternal = mode === 'external';

  useFocusEffect(
    useCallback(() => {
      setMode('external');
    }, [])
  );

  const handleTokenResponse = useCallback(
    (token: AuthTokenDto) => {
      if (token?.access_token) {
        dispatch(AppActions.setToken(token));
        // reset color scheme, so it will be reloaded from the user's profile
        setActiveColorScheme(undefined);

        router.replace('/(tabs)');
      }
    },
    [dispatch, router, setActiveColorScheme]
  );

  const { mutateAsync: registerExternal, isPending: isLoadingRegisterExternal } =
    useLoginExternalMutation({
      onSuccess: handleTokenResponse
    });

  const { mutateAsync: createAccount, isPending: isLoadingCreateAccount } =
    useAccountRegisterMutation({
      onSuccess: async ({ email, emailConfirmed }, variables) => {
        if (isEmailConfirmationRequired && !emailConfirmed) {
          router.replace('/(auth)/email-confirmation');
        } else {
          const { tenantId } = await abpTenantFindTenantByName(email!);

          if (tenantId) {
            return login({
              username: variables.emailAddress,
              password: variables.password,
              tenantId
            });
          }
        }
      }
    });

  const { mutateAsync: login, isPending: isLoadingLogin } = useLoginMutation({
    onSuccess: handleTokenResponse
  });

  const submitForm = useCallback(
    ({ username, password: { password } }: RegisterFormData) => {
      const normalizedUsername = username.toLowerCase();

      return createAccount({
        appName,
        emailAddress: normalizedUsername,
        userName: normalizedUsername,
        password,
        extraProperties: {
          [UnistocksSettings.Language]: lang.cultureName,
          [AbpSettings.TimeZone]: timeZone
        }
      });
    },
    [createAccount, lang, timeZone]
  );

  const submitLoginExternal = useCallback(
    (params: ExternalAuthenticationDto) =>
      registerExternal({
        ...params,
        [UnistocksSettings.Language]: lang,
        [AbpSettings.TimeZone]: timeZone
      }),
    [lang, registerExternal, timeZone]
  );

  const toggle = useCallback(() => {
    setMode(prev => (prev === 'external' ? 'form' : 'external'));
  }, []);

  return (
    <Page.ScrollView
      headerTransparent
      headerLogo={false}
      headerTitle={media.sm ? undefined : t('AbpAccount::Register')}
      headerBackArrow={false}
      contentContainerStyle={media.sm ? undefined : style.centerVertical}
    >
      {/* overflow visible for border shadows */}
      <HeightAnimator enabled={!isWeb}>
        <AuthContainer title={t('AbpAccount::Register')}>
          <AnimatePresence mode="wait" initial={false}>
            {isExternal ? (
              <View
                key={'external'}
                transition={'200ms'}
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              >
                <ExternalRegister
                  isSubmitting={isLoadingRegisterExternal}
                  onSubmit={submitLoginExternal}
                  toggle={toggle}
                />
              </View>
            ) : (
              <View
                key={'form'}
                transition={'200ms'}
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              >
                <RegisterForm
                  isSubmitting={isLoadingCreateAccount || isLoadingLogin}
                  onSubmit={submitForm}
                  toggle={toggle}
                />
              </View>
            )}
          </AnimatePresence>
          <Paragraph textAlign={'center'} mt={'$5'} size={'$6'}>
            {t('AbpAccount::AlreadyRegistered')}
          </Paragraph>

          <Link asChild dismissTo href={'/(auth)/login'}>
            <Anchor textAlign={'center'} fontWeight={'bold'} size={'$5'}>
              {t('::Login')}
            </Anchor>
          </Link>

          <TermsAndConditionsAccept />
        </AuthContainer>
      </HeightAnimator>
    </Page.ScrollView>
  );
};

Register.displayName = 'Register';
